/**
 * Global open api auth manager
 */

class AuthManager {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.isInitialized = false;
    this.isAuthenticating = false;
    this.authPromise = null;
    this.authConfig = null;
    this.listeners = new Set();

    // Try to load tokens from storage
    this.loadTokensFromStorage();
  }

  async initialize(config) {
    if (this.isInitialized && this.accessToken) {
      return true;
    }

    this.authConfig = {
      endpoint: config.endpoint || '/kapi/oauth2/getToken',
      method: config.method || 'POST',
      headers: config.headers || {
        'Content-Type': 'application/json'
      },
      body: config.bodyParams || {},
      params: config.queryParams || {}
    };

    try {
      await this.authenticate();
      this.isInitialized = true;
      this.notifyListeners('initialized', { success: true });
    } catch (error) {
      console.error('AuthManager: Failed to initialize auth - ', error);
      this.notifyListeners('initialized', { success: false, error });
      throw error;
    }
  }

  async authenticate() {
    if (this.isAuthenticating && this.authPromise) {
      return this.authPromise;
    }

    this.isAuthenticating = true;
    this.authPromise = this._performAuthentication();

    try {
      const result = await this.authPromise;
      this.notifyListeners('authenticated', { success: true });
      return result;
    } catch (error) {
      this.notifyListeners('authenticated', { success: false, error });
      throw error;
    } finally {
      this.isAuthenticating = false;
      this.authPromise = null;
    }
  }

  async _performAuthentication() {
    if (!this.authConfig) {
      throw new Error('Authentication configuration is missing.');
    }

    const { endpoint, headers, body, method } = this.authConfig;
    try {
      const response = await fetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status} - ${response.statusText}`);
      }

      const authData = await response.json();
      if (authData.status === true && authData.errorCode === '0') {
        this.accessToken = authData.data.access_token;
        this.refreshToken = authData.data.refresh_token;

        this.saveTokensToStorage();

        return authData;
      }
      throw new Error(`Authentication failed: ${authData.errorCode} - ${authData.message}`);
    } catch (error) {
      console.error('AuthManager: Authentication failed -', error);
      throw error;
    }
  }

  async refreshAccessToken() {
    console.log('AuthManager: Refreshing access token...')
    this.authConfig.body.nonce = this.generateNonce();
    this.authConfig.body.timestamp = this.formatTimestamp();
    return this._performAuthentication();
  }

  // 生成随机UUID作为nonce
  generateNonce() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // 格式化时间戳为 YYYY-MM-DD HH:mm:ss 格式
  formatTimestamp(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  getAccessToken() {
    return this.accessToken;
  }

  isAuthenticated() {
    return !!this.accessToken;
  }

  isReady() {
    return this.isInitialized && this.isAuthenticated();
  }

  logout() {
    this.clearTokens();
    this.isInitialized = false;
    this.authConfig = null;
    this.notifyListeners('logout', {});
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.clearTokensFromStorage();
  }

  loadTokensFromStorage() {
    try {
      this.accessToken = localStorage.getItem('__kapi_auth_manager_access_token__');
      this.refreshToken = localStorage.getItem('__kapi_auth_manager_refresh_token__');
    } catch (error) {
      console.warn('AuthManager: Error loading tokens from storage');
    }
  }

  saveTokensToStorage() {
    try {
      if (this.accessToken) {
        localStorage.setItem('__kapi_auth_manager_access_token__', this.accessToken);
      }

      if (this.refreshToken) {
        localStorage.setItem('__kapi_auth_manager_refresh_token__', this.refreshToken);
      }
    } catch (error) {
      console.warn('AuthManager: Error saving tokens to storage');
    }
  }

  clearTokensFromStorage() {
    try {
      localStorage.removeItem('__kapi_auth_manager_access_token__');
      localStorage.removeItem('__kapi_auth_manager_refresh_token__');
    } catch (error) {
      console.warn('AuthManager: Error removing tokens to storage');
    }
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  notifyListeners(event, data) {
    this.listeners.forEach((listener) => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('AuthManager: Error notifying listener')
      }
    });
  }

  async waitForAuth() {
    if (!this.isAuthenticated()) {
      return true;
    }

    if (this.isAuthenticating && this.authPromise) {
      await this.authPromise;
      return this.isAuthenticated();
    }
  }
}

const authManager = new AuthManager();

export { authManager, AuthManager };
export default authManager;

export async function initializeAuth(config) {
  return await authManager.initialize(config);
}

export function isAuthenticated() {
  return authManager.isAuthenticated();
}